import { useState } from "react";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { Logo } from "../components/Logo";
import { LogOut, Inbox, FileText, Image, Search, KeyRound } from "lucide-react";
import { EnquiriesList } from "../components/admin/EnquiriesList";
import { ContentEditor } from "../components/admin/ContentEditor";
import { ImagesManager } from "../components/admin/ImagesManager";
import { SeoEditor } from "../components/admin/SeoEditor";
import { AccountSettings } from "../components/admin/AccountSettings";

const TABS = [
  { id: "enquiries", label: "Enquiries", icon: Inbox },
  { id: "content", label: "Page Content", icon: FileText },
  { id: "images", label: "Images", icon: Image },
  { id: "seo", label: "SEO", icon: Search },
  { id: "account", label: "Account", icon: KeyRound },
];

const LoginForm = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#121926] px-5">
      <form onSubmit={submit} className="w-full max-w-sm bg-[#FBF9F5] p-9 border-t-4 border-[#B85D2A]" data-testid="admin-login-form">
        <div className="flex items-center gap-3 mb-8">
          <Logo size={34} />
          <div>
            <p className="font-serif text-base text-[#121926]">HORIZON ADMIN</p>
            <p className="font-mono text-[10px] tracking-[0.2em] text-[#8C6B4F]">CONTENT MANAGEMENT</p>
          </div>
        </div>
        {error && <p className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2" data-testid="admin-login-error">{error}</p>}
        <label className="block text-xs font-medium text-[#526071] mb-2">Email</label>
        <input data-testid="admin-email-input" type="email" className="w-full border border-[#E3DED4] px-4 py-3 text-sm mb-4 focus:outline-none focus:border-[#B85D2A]" value={email} onChange={(e) => setEmail(e.target.value)} />
        <label className="block text-xs font-medium text-[#526071] mb-2">Password</label>
        <input data-testid="admin-password-input" type="password" className="w-full border border-[#E3DED4] px-4 py-3 text-sm mb-6 focus:outline-none focus:border-[#B85D2A]" value={password} onChange={(e) => setPassword(e.target.value)} />
        <button data-testid="admin-login-button" disabled={busy} className="w-full py-3.5 bg-[#1A2533] text-[#FBF9F5] text-sm font-medium hover:bg-[#B85D2A] transition-colors disabled:opacity-60">
          {busy ? "Signing in..." : "Sign In"}
        </button>
      </form>
    </div>
  );
};

export default function Admin() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("enquiries");

  if (user === null) return <div className="min-h-screen flex items-center justify-center bg-[#121926] text-[#FBF9F5] font-mono text-sm">Loading...</div>;
  if (user === false) return <LoginForm />;

  return (
    <main className="pt-[72px] min-h-screen bg-[#F4F1EA]" data-testid="admin-panel">
      <div className="bg-[#121926]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-6 flex items-center justify-between">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#B85D2A]">Administration</p>
            <h1 className="mt-1 font-serif text-2xl text-[#FBF9F5]">Site Management</h1>
          </div>
          <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-xs text-[#C8CFD9] hover:border-[#B85D2A] hover:text-[#B85D2A] transition-colors font-mono tracking-wider">
            <LogOut size={13} /> SIGN OUT
          </button>
        </div>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex gap-1 overflow-x-auto">
          {TABS.map((tb) => (
            <button
              key={tb.id}
              data-testid={`admin-tab-${tb.id}`}
              onClick={() => setTab(tb.id)}
              className={`inline-flex items-center gap-2 px-5 py-3 text-xs font-medium tracking-wide transition-colors whitespace-nowrap ${
                tab === tb.id ? "bg-[#F4F1EA] text-[#121926]" : "text-[#9AA6B5] hover:text-white"
              }`}
            >
              <tb.icon size={14} /> {tb.label}
            </button>
          ))}
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        {tab === "enquiries" && <EnquiriesList />}
        {tab === "content" && <ContentEditor />}
        {tab === "images" && <ImagesManager />}
        {tab === "seo" && <SeoEditor />}
        {tab === "account" && <AccountSettings />}
      </div>
    </main>
  );
}
